import { Form, Space } from 'antd'
import type { NamePath } from 'antd/lib/form/interface'
import CIMSFormItem from 'components/CIMSFormItem'
import CIMSSelect from 'components/CIMSSelect'
import type { LabeledValueType } from 'components/CIMSSelect/types'
import CIMSSwitch from 'components/CIMSSwitch'
import type { NotificationField } from '../../../../../types'
import { useFieldOptions } from '../../../hooks/useFieldOptions'
import type { NotificationFieldChangeSettingsDraft } from '../types'

type FieldChangeSettingsControlProps = {
  fields: NotificationField[]
  onChange?: (settings: NotificationFieldChangeSettingsDraft | null) => void
  value?: NotificationFieldChangeSettingsDraft | null
}

type FieldChangeSettingsFieldProps = {
  fields: NotificationField[]
  name: NamePath
}

function labeledValues(value: unknown): LabeledValueType[] | undefined {
  if (!Array.isArray(value)) return undefined
  const values = value.filter(
    (item): item is LabeledValueType =>
      typeof item === 'object' &&
      item !== null &&
      'value' in item &&
      (typeof item.value === 'string' || typeof item.value === 'number')
  )
  return values.length === value.length ? values : undefined
}

function FieldChangeSettingsControl({
  fields,
  onChange,
  value = null,
}: FieldChangeSettingsControlProps) {
  const advancedSettingsId = 'notification-use-advanced-trigger-settings'
  const update = (
    key: 'allFieldsChanged' | 'anyFieldsChanged' | 'noFieldsChanged',
    nextValue: NotificationFieldChangeSettingsDraft[typeof key]
  ) => {
    if (!value) return
    onChange?.({ ...value, [key]: nextValue })
  }
  const fieldOptions = useFieldOptions(fields)

  return (
    <>
      <Space align="center" size={8} style={{ marginBottom: 24 }}>
        <CIMSSwitch
          id={advancedSettingsId}
          checked={value?.enabled ?? false}
          onChange={(enabled) => {
            if (!enabled) {
              if (value) onChange?.({ ...value, enabled: false })
              return
            }

            onChange?.({
              enabled: true,
              anyFieldsChanged: value?.anyFieldsChanged ?? [],
              allFieldsChanged: value?.allFieldsChanged ?? [],
              noFieldsChanged: value?.noFieldsChanged ?? [],
            })
          }}
        />
        <label htmlFor={advancedSettingsId}>
          Use Advanced Trigger Settings
        </label>
      </Space>
      {value?.enabled && (
        <>
          <CIMSFormItem
            htmlFor="notification-any-fields-changed"
            label="Any of these fields changed"
          >
            <CIMSSelect
              id="notification-any-fields-changed"
              mode="multiple"
              options={fieldOptions}
              placeholder="Select fields"
              value={value.anyFieldsChanged}
              onChange={(nextValue) => {
                const values = labeledValues(nextValue)
                if (values) update('anyFieldsChanged', values)
              }}
            />
          </CIMSFormItem>
          <CIMSFormItem
            htmlFor="notification-all-fields-changed"
            label="All of these fields changed"
          >
            <CIMSSelect
              id="notification-all-fields-changed"
              mode="multiple"
              options={fieldOptions}
              placeholder="Select fields"
              value={value.allFieldsChanged}
              onChange={(nextValue) => {
                const values = labeledValues(nextValue)
                if (values) update('allFieldsChanged', values)
              }}
            />
          </CIMSFormItem>
          <CIMSFormItem
            htmlFor="notification-no-fields-changed"
            label="None of these fields changed"
          >
            <CIMSSelect
              id="notification-no-fields-changed"
              mode="multiple"
              options={fieldOptions}
              placeholder="Select fields"
              value={value.noFieldsChanged}
              onChange={(nextValue) => {
                const values = labeledValues(nextValue)
                if (values) update('noFieldsChanged', values)
              }}
            />
          </CIMSFormItem>
        </>
      )}
    </>
  )
}

function FieldChangeSettingsField({
  fields,
  name,
}: FieldChangeSettingsFieldProps) {
  return (
    <Form.Item name={name} noStyle>
      <FieldChangeSettingsControl fields={fields} />
    </Form.Item>
  )
}

export default FieldChangeSettingsField
